import { initView, setInitLogo, displayError } from '@sg/pre-app-init';
import { SgAuthAdapter, BaseUserInfo } from '@sg/common';
import { isTestEnvironment } from '@sg/common/src/lib/utils/test-environment';

import { SgOidcLogin as _SgOidcLogin } from '@sg/auth/oidc';
import { SgNoAuthLogin as _SgNoAuthLogin } from '@sg/auth/no-auth';
import { SgXhrAuthLogin as _SgXhrAuthLogin } from '@sg/auth/xhr-auth';

import { Environment } from '@sg/common';
import { envApi } from '../environments/environment';

import {
  sgAdmin,
  sgNewAdmin,
  sgAnalyzer,
  sgBorrower,
  sgLender,
  sgOnBoarding,
  sgExternal,
  sgLead,
} from './app-configurations';
import { SgAppConfig } from './app-config';
export { SgApplicationConfiguration } from './app-configurations';
export { SgAppConfig } from './app-config';

import { getDeployUrl } from './utils';
export { brandingStyleLoader, getDeployUrl } from './utils';

export const currentApp: SgAppConfig = SgAppConfig.create(
  sgAdmin,
  sgNewAdmin,
  sgAnalyzer,
  sgBorrower,
  sgLender,
  sgOnBoarding,
  sgExternal,
  sgLead,
  {
    name: 'sg-ui-kit',
    authType: 'noAuth',
    branding: {
      hideSideNav: true,
      hideTopBar: true,
    },
  }
);

export function showDefaultError(error: Error): void {
  displayError('An error has occurred while trying to log in, please try again later.');
  // TODO: Loggly
}
const showInvalidAppError = () => displayError('Page not found');

const preLogo = location.pathname.includes('oidc-') ? '' : currentApp?.branding.preLogo;
initView('sg-app-root', preLogo);

// `identityReady` is fired right away, no need to instantiate it.
// this is by design, if we will init it from the main module it will be a waste of time, waiting for main to load the
// file, parse and execute.
//
// Instead, we provide this promise so the main module can use it to decide if to bootstrap angular or not.
// Most likely, the user will be resolved by the time the main executes.

let authAdapter: SgAuthAdapter;

export function getAuthAdapter(): SgAuthAdapter {
  return authAdapter;
}

function updateDynamicEnvironmentConfig(environment: Environment): Promise<void> {
  return fetch(getDeployUrl('/') + 'assets/environment.json?' + new Date().toISOString())
    .then((response) => {
      if (response.status === 200) {
        return response.json();
      } else {
        throw new Error(`Invalid configuration source - ${response.status}`);
      }
    })
    .then((data) => {
      Object.assign(environment, data);
      environment.async = false;
    });
}

/**
 * Resolves when the identity process finished.
 * The result is the `User` object when the login was successful or `null` when not.
 * This promise will never reject!
 *
 * Error's are set on the DOM internally.
 */
// TODO: only _getIdentity never throw, fix that other promises are handled and errors are set in UI.
export const identityReady = (environment: Environment) =>
  Promise.resolve(environment.async ? updateDynamicEnvironmentConfig(environment) : undefined) // get dynamic env file, if required.
    .then(() => _getIdentity(environment)) // get the identity (i.e. login or get from cache)
    .then((user) => (user ? authAdapter.getToken().then((token) => user) : user)) // get the token (this will enforce renew when needed, i.e. stale tokens.)
    .then((user) => {
      if (user) {
        initGoogleAnalytics(environment);
      }
      return user;
    })
    .catch((err) => {
      showDefaultError(err);
      console.error(err);
      authAdapter.signoutRedirect();
    });

function _getIdentity(environment: Environment): Promise<BaseUserInfo | null> {
  return new Promise<BaseUserInfo | null>((resolve) => {
    if (!currentApp) {
      showInvalidAppError();
      resolve(null);
    } else if (envApi.login && envApi.login.byPass(currentApp)) {
      if (typeof currentApp.beforeLogin === 'function') {
        currentApp.beforeLogin(environment);
      }
      authAdapter = envApi.login.adapter(currentApp, environment);
      resolve(authAdapter.user);
    } else if (currentApp.authType === 'noAuth' || isTestEnvironment()) {
      require.ensure([], () => {
        const { SgNoAuthLogin } = require('@sg/auth/no-auth');
        if (typeof currentApp.beforeLogin === 'function') {
          currentApp.beforeLogin(environment);
        }
        const sgNoAuthLogin = (authAdapter = SgNoAuthLogin.create());
        resolve(sgNoAuthLogin.user);
      });
    } else if (currentApp.authType === 'oidc') {
      require.ensure([], () => {
        const { SgOidcLogin } = require('@sg/auth/oidc');
        const redirect = (replayState?: string): void => {
          let timeoutHandler = setTimeout(() => {
            timeoutHandler = undefined;
            // TODO: It is possible that a VERY slow site, that load's after 45 secs will still load, we can't prevent that.
            displayError('It looks like loading is taking more time then it should, please try again later.');
            resolve(null);
          }, 1000 * 30); // 30 seconds.

          SgOidcLogin.get()
            .signinRedirect({
              extraQueryParams: {
                brand: currentApp.branding.name,
              },
              data: {
                pathname: replayState ? replayState : `${location.pathname}${location.search}`,
              },
            })
            .then(() => {
              if (timeoutHandler !== undefined) {
                clearTimeout(timeoutHandler);
                resolve(null);
              }
            })
            .catch((err) => {
              if (timeoutHandler !== undefined) {
                clearTimeout(timeoutHandler);
                showDefaultError(err);
                resolve(null);
              }
            });
        };

        if (typeof currentApp.beforeLogin === 'function') {
          //if the url denotes a silent login, do it
          if (location.pathname.endsWith('/none')) {
            environment.oidc.prompt = 'none';
          }
          currentApp.beforeLogin(environment);
        }

        const sOidcLogin = (authAdapter = SgOidcLogin.create(environment.oidc, currentApp.name));
        sOidcLogin.ready
          .then((user) => {
            if (!user) {
              // only redirect on valid apps;
              if (currentApp) {
                redirect();
              } else {
                showInvalidAppError();
                resolve(null);
              }
            } else {
              if (sOidcLogin.isRedirect && user.state.pathname) {
                history.pushState(null, '', user.state.pathname);
              }
              SgAppConfig.reEvaluateBranding(currentApp);
              resolve(user);
            }
          })
          .catch((err) => {
            console.error(err);
            // TODO: Should we show the error?
            // TODO: Loggly?

            const PATHNAME_DETECT_REGEXP = new RegExp(`^(?:/|\\?|().*)?$`);
            if (err.error === 'login_required' && PATHNAME_DETECT_REGEXP.test(err.state?.pathname))
              redirect(err.state?.pathname);
            else redirect();
          });
      });
    }
  });
}

function initGoogleAnalytics(environment: Environment): void {
  const appNameWithoutPrefix = currentApp.name.substring(3);
  const gaKey = environment.ga && environment.ga[appNameWithoutPrefix];
  if (!gaKey) {
    return;
  }

  setTimeout(() => {
    (function (i, s, o, g, r, a, m) {
      i['GoogleAnalyticsObject'] = r;
      (i[r] =
        i[r] ||
        function () {
          (i[r].q = i[r].q || []).push(arguments);
        }),
        (i[r].l = 1 * (new Date() as any));
      (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
      a.async = 1;
      a.src = g;
      m.parentNode.insertBefore(a, m);
    })(window, document, 'script', 'https://www.google-analytics.com/analytics.js', 'ga');
    (window as any).ga('create', gaKey, 'auto');
  });
}
